import React, { useMemo } from "react";
import { BarChart } from "@tremor/react";
import { Alert, Card, Space, Typography } from "antd";
import dayjs from "dayjs";
import isEmpty from "lodash.isempty";

import { MeasurementType } from "@fitness-app/data-models/entities/Measurement";
import { getErrorMessage } from "@fitness-app/utils";

import { useMeasurementsForRange } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/hooks/useMeasurementsForRange";
import { type CommonChartProps } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/types";

const numberFormatter = new Intl.NumberFormat("pl-PL");
const valueFormatterAbsolute = (number: number) => `${numberFormatter.format(number)} kroków`;

export const HealthStepsChart = ({ range }: CommonChartProps): React.ReactElement => {
  const {
    data: measurements = [],
    isInitialLoading,
    isError,
    error,
  } = useMeasurementsForRange(MeasurementType.HealthStepsMeasurement, range);

  const chartData: { date: string; Kroki: number }[] = useMemo(() => {
    return measurements
      .filter((measurement) => typeof measurement.data.steps === "number")
      .map((measurement) => ({
        date: dayjs(measurement.eventDate).format("DD.MM.YYYY"),
        Kroki: measurement.data.steps,
      }));
  }, [measurements]);

  const totalSteps = useMemo(() => chartData.reduce((sum, item) => sum + item.Kroki, 0), [chartData]);
  const averageSteps = chartData.length ? Math.round(totalSteps / chartData.length) : 0;

  return (
    <Card
      title="Kroki"
      type="inner"
      className="relative"
      loading={isInitialLoading}
      extra={
        <Space direction="horizontal" size={16}>
          <Typography.Text strong>
            Średnio: {isEmpty(chartData) ? "-" : numberFormatter.format(averageSteps)}
          </Typography.Text>
          <Typography.Text strong>
            Razem: {isEmpty(chartData) ? "-" : numberFormatter.format(totalSteps)}
          </Typography.Text>
        </Space>
      }
    >
      <BarChart
        className="mt-8 h-80"
        data={chartData}
        index="date"
        colors={["cyan"]}
        categories={["Kroki"]}
        showLegend={false}
        valueFormatter={valueFormatterAbsolute}
        yAxisWidth={120}
        allowDecimals={false}
        intervalType={"preserveStartEnd"}
        noDataText="Brak danych do wyświetlenia"
      />

      {isError && (
        <Alert
          message={getErrorMessage(error) || "Wystąpił błąd podczas pobierania danych"}
          type="error"
          showIcon
          className="absolute left-10 right-10 top-[55%]"
        />
      )}
      {isEmpty(chartData) && !isError && (
        <Alert
          message="Brak informacji na temat kroków klienta dla wybranego okresu"
          type="warning"
          showIcon
          className="absolute left-10 right-10 top-[55%]"
        />
      )}
    </Card>
  );
};
